import React, { memo, useCallback, useRef, useState } from 'react';
import { Text, BlockStack, Spinner, Box, EmptyState, Button, Popover, Form, TextField, Icon, InlineStack, Modal } from '@shopify/polaris';
import { ClockIcon, DeleteIcon, EditIcon } from '@shopify/polaris-icons';

// --- Import trực tiếp từ các file query của bạn ---
import { TypedTripManifest } from 'queries/trip_manifest.query';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import 'media/css/ticket.scss';
import EditOrderModal from 'entities/orders/edit_order.modal';
import EditOrderTaxModal from './components/editOrderTax.modal';
import helpers from 'helpers/index';
import ManifestItem from './components/manifestItem';
import { TypedTripData, useMutateChangeTimeForTrip, useMutateDeleteTrip } from 'queries/trip.query';
import { useField, useForm } from '@shopify/react-form';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import PaymentOrderModal from 'entities/orders/payment_order.modal';

interface TripManifestProps {
  // FIX: Chỉ nhận 'time_id', không nhận 'selectedDate' theo yêu cầu
  time_id: string | null;
  isPending?: boolean;
  manifestsData: TypedTripManifest;
  onNewTicket: (trip_manifest_id: any, type: string, position?: string[]) => void;
  tripData: TypedTripData;
  onHourChange: (h: string) => void;
  onHourTextChange: (htext: string) => void;
}

// --- Component con: Danh sách vé ---

/**
 * Component Cột 2: Hiển thị danh sách các chuyến xe (manifests)
 */
function TripManifest({ time_id, manifestsData, tripData, onNewTicket, onHourChange, onHourTextChange }: TripManifestProps) {
  const { addNotification } = useNotification();

  /** edit vé */
  const currentOrderForEditing = useRef(null);
  const [showEditTicketModal, setShowEditTicketModal] = useState(false);
  const editButtonCallbackFinal = useCallback(async (order_id: string) => {
    currentOrderForEditing.current = order_id;
    setShowEditTicketModal(true);
  }, []);

  const currentOrderForEditingTax = useRef(null);
  const [showEditTaxModal, setShowEditTaxModal] = useState(false);
  const editTaxButtonCallbackFinal = useCallback(async (order_id: string) => {
    currentOrderForEditingTax.current = order_id;
    setShowEditTaxModal(true);
  }, []);
  // new one

  /** thanh toán cho đơn hàng */
  const currentOrderForPayment = useRef(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const payButtonCallbackFinal = useCallback(async (order_id: string) => {
    currentOrderForPayment.current = order_id;
    setShowPaymentModal(true);
  }, []);
  // new one

  // FIX: Thêm lại nút "Thêm chuyến"
  // const { mutateAsync: createManifest, isPending: isCreatingManifest } = useMutateCreateTrip_manifest();
  const { mutateAsync: movingTimeFn } = useMutateChangeTimeForTrip();

  const changeTimeCallback = useCallback(
    async (time_string: string) => {
      try {
        const result = await movingTimeFn({ trip_id: tripData?.id, time_string });
        // 🚫 KHÔNG replaceState ở đây
        // ✅ Báo lên cha làm nguồn sự thật duy nhất
        onHourChange(String(result.id));
        onHourTextChange(String(result.time));
      } catch (e) {}
    },
    [movingTimeFn, onHourChange, onHourTextChange, tripData]
  );

  // const handleCreateManifest = async () => {
  //   if (!time_id) return;
  //   try {
  //     await createManifest({ time_id: time_id, trip_type: 'mainline' });
  //     addNotification('info', 'Thêm chuyến thành công!');
  //   } catch (error: any) {
  //     addNotification('error', error?.message || 'Có lỗi xảy ra');
  //   }
  // };

  /** Đổi giờ chạy */
  const [showChangeTimeModal, setShowChangeTimeModal] = useState(false);

  // thay đổi giờ

  const useFields = {
    /** create new one */
    time: useField<string>({
      value: tripData?.time,
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isValidTime(inputVal)) return 'Giờ định dạng sai (hh:mm)';
        },
      ],
    }),
  };
  const { fields, submit, submitting } = useForm({
    fields: useFields,
    async onSubmit(values) {
      if (values.time) changeTimeCallback(values.time);
      setShowChangeTimeModal(false);
      return { status: 'success', errors: [] };
    },
  });

  /** Tự động format số */

  const prevDigitsRef = useRef('');
  const handleTimeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const prevDigits = prevDigitsRef.current;
    const isAdding = digits.length > prevDigits.length;

    let formatted = raw;

    if (isAdding) {
      // chỉ format khi đang gõ thêm
      formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    } else {
      // nếu đang xóa: giữ nguyên dấu ':' nếu nó đang có trong raw
      if (raw.includes(':')) {
        formatted = raw;
      } else {
        // fallback: format lại theo digits để tránh lộn xộn
        formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
      }
    }

    prevDigitsRef.current = digits;
    fields.time.onChange(formatted);
  };

  // xóa chuyến
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: deleteTrip } = useMutateDeleteTrip();
  const deleteTripCallback = useCallback(async (trip_id: any) => {
    const confirmed = await showDeleteConfirm(
      'Xóa giờ chạy này?',
      'Bạn cần xóa vé ra khỏi chuyến, xóa hết chuyến trước mới được xóa giờ chạy. Hành động này không thể khôi phục.'
    );
    if (confirmed) {
      try {
        await deleteTrip(trip_id);
        addNotification('info', 'Xoá giờ thành công!');
      } catch (e: any) {
        let message = e.message || 'Xóa lỗi, vui lòng thử lại!';
        addNotification('error', message);
      }
    }
  }, []);

  if (!__helpers.isBigInt(time_id)) {
    return (
      <Box padding="400">
        <EmptyState heading="Chưa có chuyến xe nào trong lịch." image={''} />
      </Box>
    );
  }

  return (
    <BlockStack gap="500">
      {tripData ? (
        <Box>
          <InlineStack blockAlign="center" align="start" gap="100">
            <Popover
              preferredPosition="below"
              preventCloseOnChildOverlayClick
              active={showChangeTimeModal}
              activator={
                <Button tone="critical" icon={EditIcon} variant="plain" onClick={() => setShowChangeTimeModal(true)}>
                  {tripData?.time}
                </Button>
              }
              autofocusTarget="first-node"
              onClose={() => setShowChangeTimeModal(false)}
              sectioned
            >
              <Form onSubmit={submit}>
                <Box paddingBlock={'400'} width="250px">
                  <BlockStack gap={'400'}>
                    <TextField
                      autoComplete="off"
                      label="Giờ chạy mới"
                      placeholder=""
                      suffix="hh:mm"
                      maxLength={5}
                      prefix={<Icon source={ClockIcon} />}
                      disabled={submitting}
                      {...fields.time}
                      value={fields.time.value}
                      onChange={handleTimeChange}
                      clearButton
                      onClearButtonClick={() => handleTimeChange('')}
                    />
                    <Button tone="success" variant="primary" submit>
                      Lưu
                    </Button>
                  </BlockStack>
                </Box>
              </Form>
            </Popover>
            <div>
              <Button tone="critical" variant="plain" icon={DeleteIcon} onClick={() => deleteTripCallback(tripData?.id)}>
                Xóa bỏ
              </Button>
            </div>
          </InlineStack>
        </Box>
      ) : null}
      {manifestsData ? (
        <ManifestItem
          key={manifestsData.id + '__'}
          manifest={manifestsData}
          onNewTicket={onNewTicket}
          editButtonCallback={editButtonCallbackFinal}
          editTaxButtonCallback={editTaxButtonCallbackFinal}
          payButtonCallback={payButtonCallbackFinal}
        />
      ) : (
        <Text as="p" alignment="center">
          Chưa có chuyến xe nào cho khung giờ này
        </Text>
      )}
      <br />
      {showEditTicketModal && <EditOrderModal show={true} order_id={currentOrderForEditing.current} onClose={() => setShowEditTicketModal(false)} />}
      <EditOrderTaxModal open={showEditTaxModal} orderId={currentOrderForEditingTax.current} onClose={() => setShowEditTaxModal(false)} />
      {showPaymentModal ? (
        <PaymentOrderModal
          show={true}
          order_id={currentOrderForPayment.current}
          manifest_id={manifestsData?.id || ''}
          onClose={() => setShowPaymentModal(false)}
        />
      ) : null}
    </BlockStack>
  );
}

export default memo<TripManifestProps>(TripManifest);

